import { NUMBER_REGEXP_MAP } from './consts.ts'
import type { NumberType } from './types.ts'

const stripLeadingZeros = (value: string) => {
	return value.replace(NUMBER_REGEXP_MAP.STRIP_LEADING_ZEROS, '')
}

const digitsOnly = (value: string) => value.replace(NUMBER_REGEXP_MAP.NON_DIGITS, '')

const withSign = (sign: string, digits: string) => {
	if (digits) return `${sign}${digits}`

	return sign
}

const splitSign = (raw: string, allowNegative: boolean) => {
	const value = raw.trim()
	const sign = allowNegative && value.startsWith('-') ? '-' : ''

	return { sign, body: value.replace(/-/g, '') }
}

export const getNumbers: Record<NumberType, (sign: string, body: string) => string> = {
	INTEGER: (sign, body) => {
		const digits = stripLeadingZeros(digitsOnly(body))

		return withSign(sign, digits)
	},
	FLOAT: (sign, body) => {
		let str = body
			.replace(NUMBER_REGEXP_MAP.COMMA, '.')
			.replace(NUMBER_REGEXP_MAP.NON_DIGITS_DOT, '')

		if (!str) return withSign(sign, '')
		if (str === '.') return sign + '0.'

		const [int, ...rest] = str.split('.')
		const intValue = stripLeadingZeros(digitsOnly(int))
		const frac = digitsOnly(rest.join(''))

		if (rest.length) {
			return withSign(sign, (intValue || '0') + '.' + frac)
		}

		return withSign(sign, intValue)
	}
}

export const sanitizeNumberDraft = (
	raw: string,
	type: NumberType,
	allowNegative: boolean = true
) => {
	const { sign, body } = splitSign(raw, allowNegative)

	return getNumbers[type](sign, body)
}

const isCompleteNumber = (value: string) => {
	return (
		value !== '' &&
		value !== '-' &&
		value !== '.' &&
		!value.endsWith('.') &&
		Number.isFinite(Number(value))
	)
}

/**
 * Masks input to semver X.Y.Z format.
 * - Strips all non-digit, non-dot characters
 * - Limits each segment to 3 digits
 * - Auto-inserts a dot when a segment reaches its 3-digit cap
 * - Caps at 3 segments total
 */
const versionMask = (raw: string): string => {
	const cleaned = raw
		.replace(/[^\d.]/g, '')
		.replace(/^\.+/, '')
		.replace(/\.{2,}/g, '.')

	let result = ''
	let segmentCount = 0
	let currentSegment = ''

	for (const char of cleaned) {
		if (char === '.') {
			if (segmentCount < 2 && currentSegment.length > 0) {
				result += currentSegment + '.'
				currentSegment = ''
				segmentCount++
			}
		} else {
			if (currentSegment.length < 3) {
				currentSegment += char
			} else if (segmentCount < 2) {
				result += currentSegment + '.'
				currentSegment = char
				segmentCount++
			}
		}
	}

	return result + currentSegment
}

export const clampNumber = (value: string, min?: number, max?: number) => {
	if (!isCompleteNumber(value)) return value

	const num = Number(value)

	if (min !== undefined && num < min) return String(min)

	if (max !== undefined && num > max) return String(max)

	return value
}

/** Named built-in masks available via the `mask` prop string shorthand. */
const BUILT_IN_MASKS = {
	version: versionMask
} as const

export type BuiltInMaskName = keyof typeof BUILT_IN_MASKS

/** Resolves a mask — either a built-in name or a custom function. */
export const resolveMask = (
	mask: BuiltInMaskName | ((value: string) => string)
): ((value: string) => string) => {
	if (typeof mask === 'string') return BUILT_IN_MASKS[mask]
	return mask
}
